import {
	ArrowLeftOutlined,
	ClockCircleOutlined,
	DashboardOutlined,
	EnvironmentOutlined,
	ThunderboltOutlined,
} from "@ant-design/icons";
import { Button, Card, Descriptions, Skeleton, Statistic, Typography } from "antd";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { StatusTag, type StatusTone } from "@/shared/components/StatusTag";
import { EquipmentGpsMap } from "../components/EquipmentGpsMap";
import { useEquipment, useEquipmentGps } from "../hooks/useEquipment";
import type { Equipment, EquipmentStatus } from "../types";
import { formatDateTime, formatDurationFromDates } from "../utils/time";

const { Title, Text } = Typography;

const STATUS_LABELS: Record<EquipmentStatus, string> = {
	ACTIVE: "Faol",
	MAINTENANCE: "Ta'mirda",
	INACTIVE: "Faol emas",
	RETIRED: "Iste'foda",
};

const STATUS_TONES: Record<EquipmentStatus, StatusTone> = {
	ACTIVE: "success",
	MAINTENANCE: "warning",
	INACTIVE: "default",
	RETIRED: "error",
};

/** Formatted-or-dash helper for optional monospace values. */
function monoOrDash(value: string | null | undefined) {
	return value ? <span className="font-mono">{value}</span> : "—";
}

function EquipmentInfoCard({
	equipment,
	isLoading,
}: {
	equipment: Equipment | undefined;
	isLoading: boolean;
}) {
	if (isLoading) {
		return <Skeleton active paragraph={{ rows: 3 }} />;
	}
	if (!equipment) {
		return <Text type="secondary">Texnika ma'lumotlari topilmadi.</Text>;
	}
	return (
		<Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }} bordered>
			<Descriptions.Item label="Turi">{equipment.type?.name ?? "—"}</Descriptions.Item>
			<Descriptions.Item label="Seriya raqami">
				{monoOrDash(equipment.serialNumber)}
			</Descriptions.Item>
			<Descriptions.Item label="Davlat raqami">
				{monoOrDash(equipment.licensePlate)}
			</Descriptions.Item>
			<Descriptions.Item label="Tashkilot">{equipment.organization?.name ?? "—"}</Descriptions.Item>
			<Descriptions.Item label="Brigada">{equipment.brigade?.name ?? "—"}</Descriptions.Item>
			<Descriptions.Item label="Xodim">
				{equipment.assignedStaff?.fullname ?? "—"}
			</Descriptions.Item>
			<Descriptions.Item label="GPS qurilma (IMEI)">
				{monoOrDash(equipment.gpsDevice?.imei)}
			</Descriptions.Item>
			<Descriptions.Item label="Holati">
				<StatusTag tone={STATUS_TONES[equipment.status] ?? "default"}>
					{STATUS_LABELS[equipment.status] ?? equipment.status}
				</StatusTag>
			</Descriptions.Item>
			<Descriptions.Item label="Oxirgi signal">
				{monoOrDash(
					equipment.gpsDevice?.lastConnectionAt
						? formatDateTime(equipment.gpsDevice.lastConnectionAt)
						: null
				)}
			</Descriptions.Item>
		</Descriptions>
	);
}

export function EquipmentDetailPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();

	const { data: gpsData, isLoading: isLoadingGps } = useEquipmentGps(id ?? "");
	const { data: equipment, isLoading: isLoadingEquipment } = useEquipment(id ?? "");

	if (!id) {
		return <Navigate to="/organizations" replace />;
	}

	const trackings = gpsData?.trackings ?? [];
	const summary = gpsData?.summary;

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex items-center gap-3">
				<Button
					type="text"
					icon={<ArrowLeftOutlined />}
					onClick={() => navigate(-1)}
					size="small"
					className="text-ink-soft"
				/>
				<Title level={4} className="mb-0!">
					{equipment?.name ?? gpsData?.equipmentName ?? "—"}
				</Title>
			</div>

			{/* Equipment info */}
			<Card title="Texnika ma'lumotlari" size="small">
				<EquipmentInfoCard equipment={equipment} isLoading={isLoadingEquipment} />
			</Card>

			{/* Summary stats */}
			{isLoadingGps ? (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
					{(["speed", "max", "dist", "time"] as const).map((key) => (
						<Card key={key} size="small">
							<Skeleton active paragraph={{ rows: 1 }} />
						</Card>
					))}
				</div>
			) : summary ? (
				<div className="grid grid-cols-2 md:grid-cols-4 gap-3">
					<Card size="small" className="border-blue-100">
						<Statistic
							title={
								<span className="flex items-center gap-1 text-xs text-ink-soft">
									<DashboardOutlined /> O'rtacha tezlik
								</span>
							}
							value={summary.avgSpeed}
							precision={1}
							suffix="km/h"
							valueStyle={{ color: "#1677ff", fontSize: 22 }}
						/>
					</Card>
					<Card size="small" className="border-orange-100">
						<Statistic
							title={
								<span className="flex items-center gap-1 text-xs text-ink-soft">
									<ThunderboltOutlined /> Maks. tezlik
								</span>
							}
							value={summary.maxSpeed}
							suffix="km/h"
							valueStyle={{ color: "#f59e0b", fontSize: 22 }}
						/>
					</Card>
					<Card size="small" className="border-green-100">
						<Statistic
							title={
								<span className="flex items-center gap-1 text-xs text-ink-soft">
									<EnvironmentOutlined /> Masofa
								</span>
							}
							value={summary.distance}
							precision={2}
							suffix="km"
							valueStyle={{ color: "#22c55e", fontSize: 22 }}
						/>
					</Card>
					<Card size="small" className="border-purple-100">
						<Statistic
							title={
								<span className="flex items-center gap-1 text-xs text-ink-soft">
									<ClockCircleOutlined /> Davomiyligi
								</span>
							}
							value={formatDurationFromDates(summary.startDate, summary.endDate)}
							valueStyle={{ color: "#7c3aed", fontSize: 22 }}
						/>
					</Card>
				</div>
			) : null}

			{/* Start / End info */}
			{summary && !isLoadingGps && (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
					<Card
						size="small"
						className="border-l-4 border-l-green-500"
						title={
							<span className="flex items-center gap-2 text-success font-semibold">
								<span className="inline-block w-2.5 h-2.5 rounded-full bg-green-500" />
								Boshlanish
							</span>
						}
					>
						<Text className="text-ink font-mono">{formatDateTime(summary.startDate)}</Text>
					</Card>
					<Card
						size="small"
						className="border-l-4 border-l-red-500"
						title={
							<span className="flex items-center gap-2 text-error font-semibold">
								<span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" />
								Tugash
							</span>
						}
					>
						<Text className="text-ink font-mono">{formatDateTime(summary.endDate)}</Text>
					</Card>
				</div>
			)}

			{/* GPS Map */}
			<Card title="GPS marshrut" size="small">
				{isLoadingGps ? (
					<Skeleton active style={{ height: 400 }} />
				) : trackings.length > 0 ? (
					<EquipmentGpsMap trackings={trackings} />
				) : (
					<Text type="secondary">
						GPS ma'lumotlari topilmadi yoki texnikaga GPS qurilma ulanmagan.
					</Text>
				)}
			</Card>
		</div>
	);
}
